import { useEffect, useRef, useState } from 'react';
import { globalSearch, type GlobalSearchResult } from '../api/rondo';
import { api } from '../api/client';
import { ArcDialog } from '../components/arc/primitives/arc-primitives';
import './global-search.css';

function navigateTo(url: string): void {
  // On a Space subdomain (e.g. affilmaster.platform1.cz) the address bar drops
  // the /affilmaster prefix. Search URLs come in canonical form ('/affilmaster/...')
  // — strip the prefix when the host already selects that Space.
  let target = url;
  const host = window.location.hostname;
  const spaceFromHost = host.split('.')[0];
  const spaces = ['affilmaster', 'rondo', 'affilplatform', 'dmwk'];
  if (spaceFromHost && spaces.includes(spaceFromHost)) {
    const prefix = `/${spaceFromHost}`;
    if (target === prefix) target = '/';
    else if (target.startsWith(prefix + '/')) target = target.slice(prefix.length);
  }
  if (target !== window.location.pathname + window.location.search) {
    window.history.pushState(null, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

async function affilmasterSearch(term: string): Promise<{ results: GlobalSearchResult[] }> {
  if (term.length < 2) return { results: [] };
  const data = await api.get<{ results: GlobalSearchResult[] }>(
    `/api/v1/affilmaster/search?q=${encodeURIComponent(term)}`,
  );
  return { results: data?.results ?? [] };
}

interface Props {
  open: boolean;
  onClose: () => void;
  spaceSlug?: string;
}

export function GlobalSearch({ open, onClose, spaceSlug }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [status, setStatus] = useState('Zadej alespoň 2 znaky');

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    setQuery('');
    setResults([]);
    setSelectedIdx(0);
    setStatus('Zadej alespoň 2 znaky');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSelectedIdx(0);
      setStatus('Zadej alespoň 2 znaky');
      return;
    }
    setStatus('Hledám…');
    let cancelled = false;
    const t = setTimeout(() => {
      const runner = spaceSlug === 'affilmaster' ? affilmasterSearch(term) : globalSearch(term);
      runner
        .then((data) => {
          if (cancelled) return;
          setResults(data.results);
          setSelectedIdx(0);
          const count = data.results.length;
          setStatus(`${count} ${count === 1 ? 'výsledek' : count >= 2 && count <= 4 ? 'výsledky' : 'výsledků'}`);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setStatus('Hledání selhalo');
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, spaceSlug]);

  if (!open) return null;

  function select(idx: number): void {
    const r = results[idx];
    if (!r) return;
    onClose();
    navigateTo(r.url);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(selectedIdx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <ArcDialog open={open} onClose={onClose} size="medium" title="Hledat v Platform 1" className="p1-global-search-dialog" bodyClassName="p1-global-search-body">
              <div className="p1-global-search-field">
                <span className="p1-global-search-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  className="p1-global-search-input"
                  placeholder={spaceSlug === 'affilmaster'
                    ? 'Hledat klienty, affiliates, kontakty, doklady, kampaně, leady…'
                    : 'Hledat uživatele (i podle ID), úkoly…'}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  aria-label="Vyhledat v Platform 1"
                />
              </div>

              <div className="p1-global-search-results">
                {results.length === 0 && query.trim().length >= 2 && status !== 'Hledám…' ? (
                  <div className="p1-global-search-empty"><strong>Nic jsme nenašli</strong><small>Zkuste jiný název, e-mail, ID nebo číslo úkolu.</small></div>
                ) : status === 'Hledám…' ? (
                  <div className="p1-global-search-loading" role="status">Hledám…</div>
                ) : query.trim().length < 2 ? (
                  <div className="p1-global-search-intro">
                    <strong>Začněte psát hledaný výraz</strong>
                    <small>Stačí alespoň 2 znaky. Vyhledávání zahrnuje obsah napříč Platform 1.</small>
                  </div>
                ) : (
                  results.map((r, i) => (
                    <a
                      key={`${r.kind}-${i}`}
                      href={r.url}
                      className="p1-global-search-result"
                      data-selected={i === selectedIdx ? 'true' : undefined}
                      onMouseEnter={() => setSelectedIdx(i)}
                      onClick={(e) => {
                        e.preventDefault();
                        select(i);
                      }}
                    >
                      <span className="p1-global-search-result-icon">{r.kindLabel.slice(0, 1).toUpperCase()}</span>
                      <div className="p1-global-search-result-copy">
                        <strong>{r.title}</strong>
                        <small>{r.subtitle}</small>
                      </div>
                      <span className="p1-global-search-kind">{r.kindLabel}</span>
                    </a>
                  ))
                )}
              </div>

              <div className="p1-global-search-footer">
                <span><kbd>↑</kbd><kbd>↓</kbd> vybrat <kbd>↵</kbd> otevřít</span>
                <span>{status}</span>
              </div>
    </ArcDialog>
  );
}
